import { useContext } from 'react'
import { useQuery } from 'react-query'
import { UserContext } from '../../../hooks/UserContext'
import { userHasPermission } from '../../../navigation/NavegationRoutes'
import { fetchReplenishment } from '../../../services'
import { fetchWarehouse } from '../../../services/warehouseSevices'
import { hasActiveAppointment, canRegisterExtemporaneous } from '../../../utils/inboundAppointments'
import { UnloadWindowPanel } from './UnloadWindowPanel'
import { ExtemporaneousAction } from './ExtemporaneousAction'

// Self-contained inbound-appointment section mounted on BOTH reception detail
// screens (piece flow ShowReplenishment and box flow ShowReplenishmentBoxes).
// It fetches the plain replenishment show endpoint itself because neither
// screen's own fetch carries the nested appointment — the appointment rides
// the replenishment serializer regardless of flow. Renders null on dark
// warehouses so the monoliths stay visually unchanged until rollout.
// Gated on complete_replenishment (final-review D7): both fetches stay
// disabled and the render bails to the same "nothing to show" span for a
// user who lacks the grant — hooks stay unconditional, only `enabled` and
// the render path branch on access.
export const InboundAppointmentSection = ({ replenishmentId, warehouseId }) => {
  const { user } = useContext(UserContext)
  const hasAccess = !!(user?.id && userHasPermission(user, 'complete_replenishment'))

  const { data: replenishment, isFetched } = useQuery(['replenishment_inbound_appointment', replenishmentId], () => fetchReplenishment(replenishmentId), {
    refetchOnWindowFocus: false,
    enabled: hasAccess,
  })

  const { data: warehouse } = useQuery(['warehouse', warehouseId], () => fetchWarehouse(warehouseId), {
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    enabled: hasAccess && !!warehouseId,
  })

  if (!hasAccess) {
    return <span data-testid="inbound-appointment-section-settled" className="hidden" />
  }

  if (!isFetched || !replenishment) return null

  const showUnload = hasActiveAppointment(replenishment)
  const showExtemporaneous = canRegisterExtemporaneous(replenishment)

  if (!showUnload && !showExtemporaneous) {
    return <span data-testid="inbound-appointment-section-settled" className="hidden" />
  }

  return (
    <div className="mb-5 flex flex-col gap-5">
      {showExtemporaneous && <ExtemporaneousAction replenishmentId={replenishmentId} />}
      {/* The panel computes wall-clock prefills from the warehouse timezone
                at mount — never render it before the timezone is known (D17). */}
      {/* key: a rebooked replenishment swaps the nested appointment in place
                (invalidation refetch, no unmount) — the panel's state belongs
                to ONE appointment and must reset with the id. The unload
                fields ride the nested appointment for ops viewers (D10.1) and
                are the panel's authoritative prefill. */}
      {showUnload && warehouse?.time_zone && (
        <UnloadWindowPanel
          key={replenishment.inbound_appointment.id}
          appointmentId={replenishment.inbound_appointment.id}
          timeZone={warehouse.time_zone}
          unloadStartedAt={replenishment.inbound_appointment.unload_started_at}
          unloadEndedAt={replenishment.inbound_appointment.unload_ended_at}
        />
      )}
    </div>
  )
}
